import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Participant, CriteriaGroup } from '@/types/evaluation.types';
import { ParticipantType } from '@/types/evaluation.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Save, X, AlertCircle, Info } from 'lucide-react';

interface EditAssignmentsDialogProps {
  participant: Participant | null;
  criteriaGroups: CriteriaGroup[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (participantId: string, criteriaGroupAssignments: string[] | null) => void;
}

/**
 * Assignment mode corresponds to criteriaGroupAssignments field semantics:
 * - 'all': null (can score all groups)
 * - 'specific': [...groupIds] (can score only specific groups)
 * - 'none': [] (cannot score any groups)
 */
type AssignmentMode = 'all' | 'specific' | 'none';

export function EditAssignmentsDialog({
  participant,
  criteriaGroups,
  isOpen,
  onClose,
  onSave,
}: EditAssignmentsDialogProps) {
  const { t } = useTranslation(['participants', 'common']);

  // Assignment mode state
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('all');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Initialize form when participant changes
  useEffect(() => {
    if (participant) {
      // Determine mode from criteriaGroupAssignments
      if (participant.criteriaGroupAssignments === null) {
        setAssignmentMode('all');
        setSelectedGroupIds([]);
      } else if (participant.criteriaGroupAssignments.length === 0) {
        setAssignmentMode('none');
        setSelectedGroupIds([]);
      } else {
        setAssignmentMode('specific');
        setSelectedGroupIds(participant.criteriaGroupAssignments);
      }
    }
  }, [participant]);

  // Handle mode change
  const handleModeChange = (mode: AssignmentMode) => {
    setAssignmentMode(mode);

    // Clear selections when switching modes
    if (mode !== 'specific') {
      setSelectedGroupIds([]);
    }
  };

  // Toggle group selection
  const handleGroupToggle = (groupId: string, checked: boolean) => {
    if (checked) {
      setSelectedGroupIds([...selectedGroupIds, groupId]);
    } else {
      setSelectedGroupIds(selectedGroupIds.filter(id => id !== groupId));
    }
  };

  // Handle form submission
  const handleSave = () => {
    if (!participant) {
      return;
    }

    // Map assignment mode to criteriaGroupAssignments value
    let criteriaGroupAssignments: string[] | null;

    switch (assignmentMode) {
      case 'all':
        criteriaGroupAssignments = null;
        break;
      case 'none':
        criteriaGroupAssignments = [];
        break;
      case 'specific':
        criteriaGroupAssignments = selectedGroupIds;
        break;
    }

    onSave(participant.id, criteriaGroupAssignments);
    onClose();
  };

  // Handle dialog close
  const handleClose = () => {
    onClose();
  };

  if (!participant) {
    return null;
  }

  // Determine if we should show warnings
  const isRestrictingHost = participant.participantType === ParticipantType.Host && assignmentMode !== 'all';
  const isGrantingAccessToViewer = participant.participantType === ParticipantType.Viewer && assignmentMode !== 'none';
  const hasNoGroupsSelected = assignmentMode === 'specific' && selectedGroupIds.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t('participants:assignmentsDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('participants:assignmentsDialog.description', { name: participant.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Participant context info */}
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{participant.name}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">
              {t(`common:participantType.${participant.participantType.toLowerCase()}`)}
            </span>
          </div>

          {/* Warning for restricting Host */}
          {isRestrictingHost && (
            <Alert>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('participants:assignmentsDialog.warnings.restrictingHost')}
              </AlertDescription>
            </Alert>
          )}

          {/* Warning for granting access to Viewer */}
          {isGrantingAccessToViewer && (
            <Alert>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('participants:assignmentsDialog.warnings.grantingAccessToViewer')}
              </AlertDescription>
            </Alert>
          )}

          {/* Assignment Mode Selection */}
          <div className="space-y-3">
            <Label>{t('participants:assignmentsDialog.fields.assignmentMode.label')}</Label>
            <RadioGroup
              value={assignmentMode}
              onValueChange={(value) => handleModeChange(value as AssignmentMode)}
              className="space-y-3"
            >
              {/* All groups option */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="all" id="assignment-all" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="assignment-all" className="font-normal cursor-pointer">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.all.label')}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.all.description')}
                  </p>
                </div>
              </div>

              {/* Specific groups option */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="specific" id="assignment-specific" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="assignment-specific" className="font-normal cursor-pointer">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.specific.label')}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.specific.description')}
                  </p>
                </div>
              </div>

              {/* No groups option */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="none" id="assignment-none" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="assignment-none" className="font-normal cursor-pointer">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.none.label')}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('participants:assignmentsDialog.fields.assignmentMode.options.none.description')}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Criteria Group Selection (shown when 'specific' mode is selected) */}
          {assignmentMode === 'specific' && (
            <div className="space-y-3">
              <Label>{t('participants:assignmentsDialog.fields.criteriaGroups.label')}</Label>

              {criteriaGroups.length === 0 ? (
                <Alert>
                  <Info className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>
                    {t('participants:assignmentsDialog.fields.criteriaGroups.noCriteriaGroups')}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {hasNoGroupsSelected && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" aria-hidden="true" />
                      <AlertDescription>
                        {t('participants:assignmentsDialog.warnings.noGroupsSelected')}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2 border rounded-lg p-4 max-h-[240px] overflow-y-auto">
                    {criteriaGroups.map((group) => {
                      const isChecked = selectedGroupIds.includes(group.id);
                      const checkboxId = `criteria-group-${group.id}`;

                      return (
                        <div key={group.id} className="flex items-start space-x-3">
                          <Checkbox
                            id={checkboxId}
                            checked={isChecked}
                            onCheckedChange={(checked) => handleGroupToggle(group.id, checked as boolean)}
                          />
                          <div className="flex-1">
                            <Label htmlFor={checkboxId} className="font-normal cursor-pointer">
                              {group.name}
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              {t('participants:assignmentsDialog.fields.criteriaGroups.weightLabel', { weight: group.weight })}
                              {' • '}
                              {t('participants:assignmentsDialog.fields.criteriaGroups.criteriaCount', { count: group.criteria.length })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {t('participants:assignmentsDialog.fields.criteriaGroups.selectedCount', {
                      count: selectedGroupIds.length,
                      total: criteriaGroups.length
                    })}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            <X className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={assignmentMode === 'specific' && selectedGroupIds.length === 0}
          >
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
